import React, { useState } from "react";
import { Save, X, Edit2, Trash2, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";

export const AdminRosterTab = ({ artists, accounts = [], refresh }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Build slug -> linked user lookup
  const linkedByslug = {};
  (accounts || []).forEach((a) => {
    if (a.user?.artist_slug) linkedByslug[a.user.artist_slug] = a.user;
  });

  const startEdit = (a) => {
    setEditing(a.slug);
    setForm({
      name: a.name || "",
      role: a.role || "",
      role_pt: a.role_pt || "",
      bio: a.bio || "",
      bio_pt: a.bio_pt || "",
      image: a.image || "",
      quote: a.quote || "",
      quote_pt: a.quote_pt || "",
    });
  };

  const save = async (slug) => {
    setSubmitting(true);
    try {
      await api.patch(`/admin/artists/${slug}`, form);
      setEditing(null);
      setForm({});
      await refresh();
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (a) => {
    const linked = linkedByslug[a.slug];
    const msg = `Delete roster page "${a.name}"?\n\n` +
      (linked ? `⚠️ Account "${linked.email}" is currently linked to this page and will be unlinked.\n\n` : "") +
      `Click OK to delete the page (their account stays, just unlinked).\nClick Cancel to abort.`;
    if (!window.confirm(msg)) return;
    const purgeLedger = window.confirm(`Also wipe all ledger entries (sales/credits/payouts) for ${a.name}?\n\nClick OK to wipe ledger.\nClick Cancel to keep the ledger.`);
    try {
      await api.delete(`/admin/artists/${a.slug}`, {
        params: { purge_ledger: purgeLedger, unlink_users: true },
      });
      await refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || "Delete failed");
    }
  };

  const relink = async (a, e) => {
    const newUserId = e.target.value;
    if (!newUserId) return;
    const acc = accounts.find((x) => x.user.id === newUserId);
    if (!acc) return;
    if (!window.confirm(`Link "${a.name}" roster page to account "${acc.user.email}"? This means any sales tied to this roster will credit that account's ledger.`)) {
      e.target.value = linkedByslug[a.slug]?.id || "";
      return;
    }
    try {
      await api.patch(`/admin/artist-accounts/${newUserId}`, { artist_slug: a.slug });
      await refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || "Re-link failed");
    }
  };

  return (
    <section data-testid="admin-roster-section">
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4">/ ROSTER PROFILES</p>
      <p className="text-kult-muted text-sm mb-6">Edit each roster page, change the linked account, or delete the page.</p>

      <div className="space-y-3">
        {artists.map((a) => {
          const linked = linkedByslug[a.slug];
          return (
          <article key={a.slug} className="border border-kult-border" data-testid={`admin-roster-${a.slug}`}>
            {editing === a.slug ? (
              <form
                onSubmit={(e) => { e.preventDefault(); save(a.slug); }}
                className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3"
                data-testid={`admin-roster-form-${a.slug}`}
              >
                <Field label="NAME" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testId={`roster-name-${a.slug}`} />
                <Field label="ROLE (EN)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} testId={`roster-role-${a.slug}`} />
                <Field label="ROLE (PT)" value={form.role_pt} onChange={(e) => setForm({ ...form, role_pt: e.target.value })} testId={`roster-role-pt-${a.slug}`} />
                <Field label="QUOTE (EN)" value={form.quote || ""} onChange={(e) => setForm({ ...form, quote: e.target.value })} testId={`roster-quote-${a.slug}`} />
                <Field label="QUOTE (PT)" value={form.quote_pt || ""} onChange={(e) => setForm({ ...form, quote_pt: e.target.value })} testId={`roster-quote-pt-${a.slug}`} />
                <Textarea label="BIO (EN)" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="md:col-span-2" testId={`roster-bio-${a.slug}`} />
                <Textarea label="BIO (PT)" value={form.bio_pt} onChange={(e) => setForm({ ...form, bio_pt: e.target.value })} className="md:col-span-2" testId={`roster-bio-pt-${a.slug}`} />
                <div className="md:col-span-2">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">PROFILE PHOTO</p>
                  <ImageUpload value={form.image ? [form.image] : []} onChange={(imgs) => setForm({ ...form, image: imgs[0] || "" })} max={1} testIdPrefix={`roster-image-${a.slug}`} recommendedSize="1200×1500 (4:5 portrait)" />
                </div>
                <div className="md:col-span-2 flex gap-2">
                  <button type="submit" disabled={submitting} className="bg-kult-pink text-black font-display text-xl py-3 px-6 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center gap-2" data-testid={`roster-save-${a.slug}`}>
                    <Save size={18} /> SAVE
                  </button>
                  <button type="button" onClick={() => { setEditing(null); setForm({}); }} className="border border-kult-border px-6 py-3 font-display text-xl inline-flex items-center gap-2">
                    <X size={18} /> CANCEL
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                <div className="md:col-span-4 flex gap-3 items-center min-w-0">
                  {a.image && <img src={a.image} alt="" className="w-16 h-16 object-cover grayscale flex-shrink-0" />}
                  <div className="min-w-0">
                    <h3 className="font-display text-2xl truncate">{a.name}</h3>
                    <p className="font-mono text-[10px] tracking-[0.25em] text-kult-yellow mt-0.5">{a.role}</p>
                  </div>
                </div>
                <div className="md:col-span-3">
                  <label className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">LINKED ACCOUNT</label>
                  <select
                    value={linked?.id || ""}
                    onChange={(e) => relink(a, e)}
                    className="w-full bg-kult-black border border-kult-border px-2 py-2 font-mono text-xs"
                    data-testid={`roster-link-${a.slug}`}
                  >
                    <option value="">— unlinked —</option>
                    {accounts.map((acc) => (
                      <option key={acc.user.id} value={acc.user.id}>
                        {acc.user.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3 flex gap-2 items-center font-mono text-[10px]">
                  <a href={`/artists/${a.slug}`} target="_blank" rel="noopener noreferrer" className="border border-kult-border px-2 py-2 tracking-[0.25em] hover:border-kult-pink inline-flex items-center gap-1" data-testid={`roster-page-link-${a.slug}`}>
                    PAGE <ExternalLink size={10} />
                  </a>
                  <a href={`/store/${a.slug}`} target="_blank" rel="noopener noreferrer" className="border border-kult-yellow text-kult-yellow px-2 py-2 tracking-[0.25em] hover:bg-kult-yellow hover:text-black inline-flex items-center gap-1" data-testid={`roster-store-link-${a.slug}`}>
                    STORE <ExternalLink size={10} />
                  </a>
                </div>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button onClick={() => startEdit(a)} className="p-2 border border-kult-border hover:border-kult-pink hover:text-kult-pink" data-testid={`admin-roster-edit-${a.slug}`} aria-label="Edit">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => del(a)} className="p-2 border border-red-500 text-red-400 hover:bg-red-500/10" data-testid={`admin-roster-delete-${a.slug}`} aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )}
          </article>
          );
        })}
      </div>
    </section>
  );
};

function Field({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <input {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Textarea({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <textarea rows={3} {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
